import { cachedFetch, getCached, setCached, invalidate } from '../../core/cache';
import { qsa, on, lockBodyScroll, unlockBodyScroll } from '../../core/dom';
import { activateFocusTrap, type FocusTrap } from '../../core/focus-trap';
import type { NavModuleInit } from '../../core/types';

type MemberMenuItem = { url: string; label: string; badge?: 'new' | 'dot' | null };

const AVATAR_CACHE_KEY = 'avatar:picture';
const COMPARISON_HISTORY_CACHE_KEY = 'comparison-history';
const COMPARISON_HISTORY_TTL_MS = 30 * 60 * 1000;
const COMPARISON_HISTORY_LIMIT = 3;
const COMPARISON_HISTORY_QUERY = {
  query:
    'query GetComparisonHistoryNiches($limit: Int) { getComparisonHistoryNiches(limit: $limit) { nicheName url } }',
  variables: { limit: COMPARISON_HISTORY_LIMIT },
};

type ComparisonHistoryNiche = { nicheName: string; url: string };

const isMobile = (): boolean => !window.matchMedia('(min-width: 960px)').matches;

const init: NavModuleInit = ({ session, state, bus }) => {
  const avatars = qsa<HTMLButtonElement>(
    '[data-gn-trigger="user-menu"], [data-testid^="global-nav-user-avatar-button"]',
  );
  if (avatars.length === 0) return;

  let appliedPicture: string | null = null;

  const applyPicture = (picture: string) => {
    if (appliedPicture === picture) return;
    appliedPicture = picture;
    avatars.forEach((btn) => {
      const wrap = btn.querySelector<HTMLElement>('.round-wrapper');
      if (!wrap) return;
      const existing = wrap.querySelector<HTMLImageElement>('img[data-gn-avatar]');
      if (existing) {
        if (existing.src !== picture) existing.src = picture;
        return;
      }
      wrap.innerHTML = '';
      const img = document.createElement('img');
      img.src = picture;
      img.alt = 'User avatar';
      img.setAttribute('data-gn-avatar', '');
      img.className = 'w-[24px] !h-[24px] mr-[2px] rounded-full';
      wrap.appendChild(img);
    });
  };

  if (document.body.classList.contains('user-authenticated')) {
    const cached = getCached<string>(AVATAR_CACHE_KEY, Infinity);
    if (cached) applyPicture(cached);
  }

  const tryApplyFromSession = () => {
    if (!session.isLoggedIn()) {
      invalidate(AVATAR_CACHE_KEY);
      return;
    }
    const sd = session.getSessionData() as { picture?: string } | null;
    const picture = sd?.picture;
    if (typeof picture === 'string' && picture) {
      setCached(AVATAR_CACHE_KEY, picture);
      applyPicture(picture);
    }
  };

  tryApplyFromSession();
  const offAuth = session.onAuthChange(tryApplyFromSession);

  const props = (state.membersProps ?? {}) as Record<string, unknown>;
  const menuItems = (props.memberMenuItems as MemberMenuItem[] | undefined) ?? [];
  const logoutUrl = (props.logoutUrl as string | undefined) ?? '#';

  let comparisonHistory: MemberMenuItem[] =
    getCached<MemberMenuItem[]>(COMPARISON_HISTORY_CACHE_KEY, COMPARISON_HISTORY_TTL_MS) ?? [];

  let comparisonHistoryFetched = false;

  const fetchComparisonHistory = async (): Promise<void> => {
    if (comparisonHistoryFetched) return;
    comparisonHistoryFetched = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mgr: any = session.getRawManager();
      if (typeof mgr?.openSession === 'function') {
        await mgr.openSession();
      }
      if (!session.isLoggedIn() || typeof mgr?.getGraphqlClient !== 'function') {
        comparisonHistoryFetched = false;
        return;
      }
      const client = mgr.getGraphqlClient();
      if (!client || typeof client.request !== 'function') {
        comparisonHistoryFetched = false;
        return;
      }

      const data = await cachedFetch<MemberMenuItem[]>(
        COMPARISON_HISTORY_CACHE_KEY,
        async () => {
          const result = await client.request(COMPARISON_HISTORY_QUERY);
          if (result?.errors?.length) {
            throw new Error(`GraphQL: ${JSON.stringify(result.errors)}`);
          }
          const niches: ComparisonHistoryNiche[] = result?.data?.getComparisonHistoryNiches ?? [];
          return niches
            .filter((n) => !!n.nicheName)
            .map((n) => ({ url: n.url, label: n.nicheName }));
        },
        COMPARISON_HISTORY_TTL_MS,
      );
      comparisonHistory = data;
      if (data.length === 0) {
        // empty cache locks the popover empty for 30 min — retry on next auth signal
        invalidate(COMPARISON_HISTORY_CACHE_KEY);
        comparisonHistoryFetched = false;
      }
    } catch (error) {
      comparisonHistoryFetched = false;
      // eslint-disable-next-line no-console
      console.warn('gn2 user-menu: comparison history fetch failed', error);
    }
  };

  void fetchComparisonHistory();

  const offComparisonAuth = session.onAuthChange(() => {
    if (session.isLoggedIn()) {
      void fetchComparisonHistory();
    }
  });

  let panel: HTMLElement | null = null;
  let trap: FocusTrap | null = null;
  let backdrop: HTMLElement | null = null;
  let scrollLocked = false;

  const addMobileChrome = () => {
    if (!isMobile()) return;
    backdrop = document.createElement('div');
    backdrop.className = 'gn-mobile-backdrop is-visible';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', () => setOpen(false));
    document.body.appendChild(backdrop);
    lockBodyScroll();
    scrollLocked = true;
  };

  const removeMobileChrome = () => {
    backdrop?.remove();
    backdrop = null;
    if (scrollLocked) {
      unlockBodyScroll();
      scrollLocked = false;
    }
  };

  const renderItem = (item: MemberMenuItem, isSignOut = false): HTMLLIElement => {
    const li = document.createElement('li');
    li.className = 'gn-user-menu__item';
    const a = document.createElement('a');
    a.href = item.url;
    a.className = 'gn-user-menu__link';
    a.setAttribute('role', 'menuitem');

    const labelSpan = document.createElement('span');
    labelSpan.className = 'gn-user-menu__link-text';
    labelSpan.textContent = item.label;
    a.appendChild(labelSpan);

    if (item.badge === 'new') {
      const badge = document.createElement('span');
      badge.className = 'gn-user-menu__badge';
      badge.textContent = 'New';
      a.appendChild(badge);
    }

    if (isSignOut) {
      const loader = document.createElement('span');
      loader.className = 'gn-loader';
      loader.setAttribute('aria-hidden', 'true');
      loader.hidden = true;
      a.appendChild(loader);

      a.addEventListener('click', (e) => {
        e.preventDefault();
        loader.hidden = false;
        a.style.pointerEvents = 'none';
        session.logout();
        // session.logout() triggers Auth0 logout + redirect — SSR on the next
        // page render reflects logged-out state. Local UI keeps the loader
        // until the redirect kicks in.
      });
    }

    li.appendChild(a);
    return li;
  };

  const buildPanel = (): HTMLElement => {
    const el = document.createElement('div');
    el.className = 'gn-user-menu';
    el.setAttribute('role', 'menu');

    const ul = document.createElement('ul');
    ul.className = 'gn-user-menu__list';

    menuItems.forEach((item) => ul.appendChild(renderItem(item)));
    ul.appendChild(renderItem({ url: logoutUrl, label: 'Sign out' }, true));

    if (comparisonHistory.length > 0) {
      const divider = document.createElement('li');
      divider.className = 'gn-user-menu__divider';
      const dividerLabel = document.createElement('span');
      dividerLabel.textContent = 'Keep comparing';
      divider.appendChild(dividerLabel);
      ul.appendChild(divider);

      comparisonHistory.forEach((item) => ul.appendChild(renderItem(item)));
    }

    el.appendChild(ul);
    return el;
  };

  const setOpen = (open: boolean, anchorEl?: HTMLButtonElement) => {
    if (open && !session.isLoggedIn()) {
      // Guest users don't have a user menu — Join button handles auth flow.
      return;
    }
    if (open) {
      if (panel) {
        panel.remove();
        panel = null;
      }
      panel = buildPanel();
      const anchor =
        anchorEl ?? avatars.find((a) => a.offsetParent !== null) ?? avatars[avatars.length - 1]!;
      const isDesktop = window.matchMedia('(min-width: 960px)').matches;
      if (isDesktop) {
        const rect = anchor.getBoundingClientRect();
        const nav = anchor.closest('nav') ?? document.getElementById('desktop-top-nav-ssr');
        const navBottom = nav?.getBoundingClientRect().bottom ?? rect.bottom;
        panel.style.position = 'absolute';
        panel.style.top = `${navBottom + window.scrollY - 6}px`;
        panel.style.right = `${Math.max(8, window.innerWidth - rect.right + 8)}px`;
        panel.style.left = '';
        panel.style.width = '';
        panel.classList.remove('gn-user-menu--mobile');
      } else {
        // Mobile top-nav is ~60px tall (py-3 + ~36px content). Use the same
        // value the notifications dropdown uses so both panels open at the
        // same Y position when the user toggles between them.
        panel.style.position = 'fixed';
        panel.style.top = '60px';
        panel.style.left = '0';
        panel.style.right = '';
        panel.style.width = '100vw';
        panel.classList.add('gn-user-menu--mobile');
      }
      document.body.appendChild(panel);
      addMobileChrome();
      trap = activateFocusTrap(panel);
    } else if (panel) {
      trap?.destroy();
      trap = null;
      panel.remove();
      removeMobileChrome();
    }
    avatars.forEach((a) => a.setAttribute('aria-expanded', String(open)));
    bus.emit('user-menu:toggle', { open });
  };

  const offBusNotif = bus.on('notifications:open', () => {
    if (isOpen()) setOpen(false);
  });
  const offBusSlide = bus.on('slide-out:toggle', (d) => {
    if (d?.open && isOpen()) setOpen(false);
  });

  const isOpen = () => !!panel && panel.isConnected;

  const onAvatarClick = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(!isOpen(), e.currentTarget as HTMLButtonElement);
  };

  const onDocumentClick = (e: MouseEvent) => {
    if (!panel) return;
    if (panel.contains(e.target as Node)) return;
    if (avatars.some((a) => a.contains(e.target as Node))) return;
    setOpen(false);
  };

  const onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen()) setOpen(false);
  };

  const offClicks = avatars.map((a) => on(a, 'click', onAvatarClick as (e: Event) => void));
  const offDoc = on(document, 'click', onDocumentClick as (e: Event) => void);
  const offEsc = on(document, 'keydown', onEscape as (e: Event) => void);

  return {
    destroy: () => {
      offClicks.forEach((off) => off());
      offDoc();
      offEsc();
      offAuth();
      offComparisonAuth();
      offBusNotif();
      offBusSlide();
      setOpen(false);
    },
  };
};

export default init;
